import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Layout from "@components/layout/Layout";
import WorkflowPanels from "@components/form/WorkflowPanels";
import InfoPanel from "@components/form/InfoPanel";
import Circle from "@components/shared/Circle";
import Loading from "@components/shared/Loading";
import { useReportTemplate } from "@hooks/query/useReportTemplate";
import {
  AdhocReportProvider,
  useAdhocReport,
} from "@context/AdhocReportContext";
import StepFields from "@components/reports/adhoc/StepFields";
import StepFilters from "@components/reports/adhoc/StepFilters";
import StepPreview from "@components/reports/adhoc/StepPreview";
import StepCompleted from "@components/reports/adhoc/StepCompleted";
import ReportingWorkspace from "@components/reports/adhoc/ReportingWorkspace";
import { useAppContext } from "@context/AppContext";
import { toastError } from "@utils/toast";

/**
 * CR-SEQ-2026-025 — the replacement General Report builder, mirroring the
 * DEQ flow: three working steps (columns, filters, review & run) plus a
 * completion step. Reports are named when they are saved — as a template or
 * a snapshot — not up front.
 *
 * `?template=<id>` runs a saved template: straight to the run step,
 * read-only apart from the dates, as DEQ's run page is. `?editTemplate=<id>`
 * opens the template in the builder for editing, DEQ's edit journey.
 */
const BuilderInner: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    currentStep,
    templateName,
    loadTemplate,
    isCatalogLoading,
    isTemplateRun,
  } = useAdhocReport();
  const { checkPermissions, loggedInInfo } = useAppContext();
  const [searchParams] = useSearchParams();
  const { templateId: routeTemplateId } = useParams();
  const navigate = useNavigate();
  // Keep the former query-string link working for bookmarks while new links
  // use the route that describes the task: running a template, not creating
  // a new report.
  const runTemplateId =
    routeTemplateId ?? searchParams.get("template") ?? undefined;
  const editTemplateId = searchParams.get("editTemplate") ?? undefined;
  const { data: template, isError } = useReportTemplate(
    runTemplateId ?? editTemplateId,
  );
  const loadedTemplateId = useRef<string>();
  const canManageTemplate = Boolean(
    template &&
      (checkPermissions(["SuperAdmin"]) ||
        template.createdByUserId === loggedInInfo?.userDetails?.id),
  );

  useEffect(() => {
    // Once per template. A background refetch hands back a new object, and
    // re-applying it would throw away an edit in progress.
    if (
      template &&
      !isCatalogLoading &&
      loadedTemplateId.current !== template.id
    ) {
      if (editTemplateId && !canManageTemplate) {
        loadedTemplateId.current = template.id;
        toastError(t("reporting.template_edit_forbidden"));
        navigate("/polestar/reports/adhoc#0", { replace: true });
        return;
      }
      loadedTemplateId.current = template.id;
      loadTemplate(template, runTemplateId ? "run" : "edit");
    }
  }, [
    template,
    isCatalogLoading,
    editTemplateId,
    canManageTemplate,
    loadTemplate,
    runTemplateId,
    navigate,
    t,
  ]);

  useEffect(() => {
    // A dead template link must not strand the operator on an empty builder.
    if (isError) {
      toastError(t("reporting.template_load_failed"));
      navigate("/polestar/reports/adhoc#0");
    }
  }, [isError, navigate, t]);

  // Each step carries the guidance panel the rest of the console uses, so
  // the operator is told what the step decides before they decide it.
  const info = (...keys: string[]) => (
    <InfoPanel>
      <div className="space-y-4">
        {keys.map(key => (
          <p key={key}>{t(`reporting.${key}`)}</p>
        ))}
      </div>
    </InfoPanel>
  );

  const columnsInfo = (
    <InfoPanel>
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-700">
          {t("reporting.builder_info_columns_title")}
        </h3>
        <p>{t("reporting.builder_info_columns1")}</p>
        <p>{t("reporting.builder_info_columns2")}</p>
        <p>{t("reporting.builder_info_columns3")}</p>
        <p>{t("reporting.builder_info_columns4")}</p>
        <p>
          <strong>{t("reporting.builder_info_columns_tip")}</strong>
        </p>
      </div>
    </InfoPanel>
  );

  const steps = [
    {
      key: "general_step2_title",
      panel: <StepFields />,
      infoPanel: columnsInfo,
    },
    {
      key: "general_step3_title",
      panel: <StepFilters />,
      infoPanel: info("builder_info_filters1", "builder_info_filters2"),
    },
    {
      key: "general_step4_title",
      panel: <StepPreview />,
      infoPanel: info(
        "builder_info_review1",
        "builder_info_review2",
        "builder_info_review3",
      ),
    },
    {
      key: "general_step5_title",
      panel: <StepCompleted />,
      infoPanel: info("builder_info_done1"),
    },
  ];

  // Running a saved template is not a wizard: nobody walked the steps, so a
  // rail marking them done would be false. It renders the way every other
  // Polestar report page renders — the report, with its filter bar, full
  // width.
  if (runTemplateId) {
    const runInfoPanel = info(
      "template_run_info1",
      "template_run_info2",
      ...(checkPermissions(["GenerateReports"]) ? ["template_run_info3"] : []),
    );
    const displayName = templateName ?? template?.name;

    return (
      <Layout
        permissions={["ViewReports"]}
        breadcrumb={[
          { label: t("dashboard.dashboard"), href: "/polestar" },
          {
            label: t("reporting.hub_title"),
            href: "/polestar/reports/adhoc#0",
          },
          { label: t("reporting.template_run_page_title") },
        ]}
        title={displayName ?? t("reporting.templates_title")}
        status={
          template ? (
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-primary-3">
              {t("reporting.template_run_badge")}
            </span>
          ) : undefined
        }
      >
        <ReportingWorkspace activeSection="templates" infoPanel={runInfoPanel}>
          {!template || isCatalogLoading || !isTemplateRun ? (
            <div className="grow p-4">
              <Loading />
            </div>
          ) : (
            <StepPreview />
          )}
        </ReportingWorkspace>
      </Layout>
    );
  }

  return (
    <Layout
      permissions={["ViewReports", "GenerateReports"]}
      breadcrumb={[
        { label: t("dashboard.dashboard"), href: "/polestar" },
        { label: t("reporting.hub_title"), href: "/polestar/reports/adhoc" },
        { label: templateName ?? t("reporting.new_general_report") },
      ]}
      title={templateName ?? t("reporting.new_general_report")}
    >
      <WorkflowPanels
        selectedStep={currentStep}
        steps={steps.map((step, index) => ({
          label: (
            <div className="flex items-center gap-2">
              <Circle
                className="shrink-0 w-8 h-8"
                isSuccess={index < currentStep}
                isActive={index === currentStep}
              >
                {index + 1}
              </Circle>
              {t(`reporting.${step.key}`)}
            </div>
          ),
          panel: step.panel,
          infoPanel: step.infoPanel,
        }))}
      />
    </Layout>
  );
};

const AdhocGeneralReports: React.FunctionComponent = () => {
  return (
    <AdhocReportProvider>
      <BuilderInner />
    </AdhocReportProvider>
  );
};

export default AdhocGeneralReports;
